import invariant        from 'invariant';
import { createAction } from 'redux-actions';
import _                from 'underscore';
import camelize         from 'underscore.string/camelize';
import underscored      from 'underscore.string/underscored';
import {
  ACTIONS,
  METHOD_DEL,
  METHOD_GET,
  METHOD_POST,
  METHOD_PUT,
  METHODS_BY_ACTION,
  STAGES
}                       from './apiResourceActions.const';
import resourcesConfig  from '../../../config/resources.config';


//---
// List actions


const buildActions = _.memoize(({resource, action, method}) => {

  validateResource(resource);

  validateAction(action);

  method = prepareMethod({method, action});

  const request = buildSynchronousAction({
    resource,
    action,
    method,
    stage:          STAGES.REQUEST,
    payloadFactory: actionPayloadFactories[action][method][STAGES.REQUEST].payload || null,
    metaFactory:    actionPayloadFactories[action][method][STAGES.REQUEST].meta || null,
  });

  const success = buildSynchronousAction({
    resource,
    action,
    method,
    stage:          STAGES.SUCCESS,
    payloadFactory: actionPayloadFactories[action][method][STAGES.SUCCESS].payload || null,
    metaFactory:    actionPayloadFactories[action][method][STAGES.SUCCESS].meta || null,
  });

  const failure = buildSynchronousAction({
    resource,
    action,
    method,
    stage:          STAGES.FAILURE,
    payloadFactory: actionPayloadFactories[action][method][STAGES.FAILURE].payload || null,
    metaFactory:    actionPayloadFactories[action][method][STAGES.FAILURE].meta || null,
  });

  return {request, success, failure};

}, props => JSON.stringify(props));


const actionPayloadFactories = {

  [ACTIONS.LIST]: {
    [METHOD_GET]: {
      [STAGES.REQUEST]: {
        payload: ({url}) => ({method: METHOD_GET, url}),
      },
      [STAGES.SUCCESS]: {
        payload: ({receivedData}) => ({receivedData}),
        meta:    ({url}) => ({method: METHOD_GET, url}),
      },
      [STAGES.FAILURE]: {
        meta: (error, {url}) => ({method: METHOD_GET, url}),
      },
    },
  },

  [ACTIONS.CREATE]: {
    [METHOD_POST]: {
      [STAGES.REQUEST]: {
        /**
         *
         * @param {object}  dataToBeSaved Data to be saved.
         * @param {?string} url           (optional) URL to use when making API request to create resource.
         *                                This is useful when adding items to subresource collections
         *                                e.g. POST /shortlists/{shortlistId}/services to add a service to a shortlist
         * @param {?string} alias
         * @return {{dataToBeSaved: *, url: *, alias: *}}
         */
        payload: ({dataToBeSaved, url = null, alias = null}) => ({dataToBeSaved, method: METHOD_POST, url, alias}),
      },
      [STAGES.SUCCESS]: {
        payload: ({receivedData}) => ({receivedData}),
        meta:    ({dataToBeSaved, alias}) => ({dataToBeSaved, method: METHOD_POST, alias}),
      },
      [STAGES.FAILURE]: {
        meta: (error, {dataToBeSaved, alias}) => ({dataToBeSaved, method: METHOD_POST, alias}),
      },
    },
  },

  [ACTIONS.SHOW]: {
    [METHOD_GET]: {
      [STAGES.REQUEST]: {
        payload: ({url, alias, id}) => ({method: METHOD_GET, url, alias, id}),
      },
      [STAGES.SUCCESS]: {
        payload: ({receivedData}) => ({receivedData}),
        meta:    ({url, alias, id}) => ({method: METHOD_GET, url, alias, id}),
      },
      [STAGES.FAILURE]: {
        meta: (error, {url, alias, id}) => ({method: METHOD_GET, url, alias, id}),
      },
    },
  },

  [ACTIONS.UPDATE]: {
    [METHOD_GET]: {
      [STAGES.REQUEST]: {
        payload: ({id, alias, url}) => ({method: METHOD_GET, id, alias, url}),
      },
      [STAGES.SUCCESS]: {
        payload: ({receivedData}) => ({receivedData}),
        meta:    ({id, alias, url}) => ({method: METHOD_GET, id, alias, url}),
      },
      [STAGES.FAILURE]: {
        meta: (error, {id, alias, url}) => ({method: METHOD_GET, id, alias, url}),
      },
    },
    [METHOD_PUT]: {
      [STAGES.REQUEST]: {
        payload: ({url, dataToSave}) => ({method: METHOD_PUT, url, dataToSave}),
      },
      [STAGES.SUCCESS]: {
        payload: ({receivedData}) => ({receivedData}),
        meta:    ({url, dataToSave}) => ({method: METHOD_PUT, url, dataToSave}),
      },
      [STAGES.FAILURE]: {
        meta: (error, {url, dataToSave}) => ({method: METHOD_PUT, url, dataToSave}),
      },
    },
  },

  [ACTIONS.DEL]: {
    [METHOD_DEL]: {
      [STAGES.REQUEST]: {
        payload: ({url}) => ({url}),
      },
      [STAGES.SUCCESS]: {
        meta: ({url}) => ({url}),
      },
      [STAGES.FAILURE]: {
        meta: (error, {url}) => ({url}),
      },
    },
  },

};


/**
 * Creates the action creators for the list action
 *
 * @param {string} resource
 */
export function buildListActions(resource) {

  return buildActions({resource, action: ACTIONS.LIST});

}


//---
// Create actions

export function buildCreateActions(resource, method = null) {

  return buildActions({resource, action: ACTIONS.CREATE, method});
}


//---
// Show actions

export function buildShowActions(resource) {

  return buildActions({resource, action: ACTIONS.SHOW});

}


//---
// Update actions

export function buildUpdateActions(resource, method) {

  return buildActions({resource, action: ACTIONS.UPDATE, method});

}


//---
// Delete actions

export function buildDeleteActions(resource) {

  return buildActions({resource, action: ACTIONS.DEL});
}


//--
// All, by action group

export const buildMethodsByActionGroup = {
  [ACTIONS.LIST]:   buildListActions,
  [ACTIONS.CREATE]: buildCreateActions,
  [ACTIONS.SHOW]:   buildShowActions,
  [ACTIONS.UPDATE]: buildUpdateActions,
  [ACTIONS.DEL]:    buildDeleteActions,
};


/**
 * Creates action creators for the given resource
 *
 * @param {string} resource
 * @param {string} action
 * @param {?string} method
 * @param {string} stage
 * @param {?function} payloadFactory
 * @param {?function} metaFactory
 */
const buildSynchronousAction = ({resource, action, method = null, stage, payloadFactory = null, metaFactory = null}) => {

  validateResource(resource);

  validateAction(action);

  const methodToUse = prepareMethod({method, action});

  invariant(STAGES[stage], `Unknown stage (${stage}).  Possible values are '${Object.keys(STAGES).join('\', \'')}'`);

  const actionType = `API:${underscored(resource)}:${action}:${methodToUse}:${stage}`.toUpperCase();

  return createAction(actionType, payloadFactory, metaFactory);

};


const apiActionPattern = new RegExp('^API:([^:]+):([^:]+):([^:]+):([^:]+)$');


export const isApiAction = (action) => !!parseApiAction(action);

export const isApiRequestAction = (action) => isApiAction(action) && (STAGES.REQUEST === parseApiAction(action).stage);

export const isApiSuccessAction = (action) => isApiAction(action) && (STAGES.SUCCESS === parseApiAction(action).stage);

export const isApiFailureAction = (action) => isApiAction(action) && (STAGES.FAILURE === parseApiAction(action).stage);


export const parseApiAction = _.memoize(
  (action) => {

    const match = action.type.match(apiActionPattern);

    if (!match) {

      return false;

    }

    let [, resourceName, actionGroup, method, stage] = match;

    resourceName = camelize(resourceName.toLowerCase().replace('_', '-'));

    return {resourceName, actionGroup, method, stage};

  },
  (action) => action.type
);


//---
// Validation methods
//---

function validateResource(resource) {
  invariant(
    resourcesConfig[resource],
    `Unknown resource (${resource}).  Possible values are '${Object.keys(resourcesConfig).join('\', \'')}'`
  );
}


function validateAction(action) {
  invariant(
    ACTIONS[action],
    `Unknown action (${action}).  Possible values are '${Object.keys(ACTIONS).join('\', \'')}'`
  );
}


function prepareMethod({action, method = null}) {

  validateAction(action);

  if (!method) {
    invariant(
      1 === Object.keys(METHODS_BY_ACTION[action]).length,
      `Missing method.  Method is required when there are multiple possible values for action '${action}.  ` +
      `Possible values are ${Object.keys(METHODS_BY_ACTION[action]).join('\', \'')}`
    );
  }

  const methodToUse = method || Object.values(METHODS_BY_ACTION[action])[0];

  validateMethod(action, methodToUse);

  return methodToUse;

}


function validateMethod(action, methodToUse) {
  invariant(
    METHODS_BY_ACTION[action][methodToUse],
    `Unknown method (${methodToUse}) for action (${action}).  ` +
    `Possible values are '${Object.keys(METHODS_BY_ACTION[action]).join('\', \'')}'`
  );
}


// WEBPACK FOOTER //
// ./src/actions/utils/apiResources/buildSyncActions.js